<template>
  <div class="canvas-workspace">
    <div class="controls-panel">
      <div class="nav-control">
        <span class="control-label">Navigate</span>
        <div
          class="joystick-pad"
          ref="joystickPad"
          @mousedown.prevent="onJoystickStart"
          @touchstart.prevent="onJoystickTouchStart"
        >
          <div class="joystick-crosshair"></div>
          <div class="joystick-ring"></div>
          <div
            class="joystick-knob"
            :class="{ 'joystick-knob--active': isDragging }"
            :style="{
              transform: `translate(${knobX}px, ${knobY}px)`,
              transition: isDragging ? 'none' : 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }"
          ></div>
        </div>
        <span class="joystick-hint">Drag to scroll</span>
      </div>

      <div class="main-workspace">
        <div class="workspace-header">
          <div class="position-chip">
            <span class="coordinates">{{ posGlobalX }}, {{ posGlobalY }}</span>
            <span class="coordinates-label">Position</span>
          </div>
        </div>

        <div class="canvas-area" @wheel.prevent="onWheel">
          <BrickTable
            :rows="rows"
            :posGlobalX="posGlobalX"
            :posGlobalY="posGlobalY"
            @cell-clicked="sendNotification"
            @dimensions-changed="$emit('dimensions-changed', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BrickTable from './brick-table.vue';

export default {
  components: {
    BrickTable,
  },
  name: 'MAinTable',
  props: {
    posGlobalX: {
      type: Number,
      required: true,
    },
    posGlobalY: {
      type: Number,
      required: true,
    },
    rows: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      knobX: 0,
      knobY: 0,
      isDragging: false,
      joystickRadius: 34,
    };
  },
  methods: {
    onJoystickStart(e) {
      this.isDragging = true;
      this._onMove = this._handleMouseMove.bind(this);
      this._onEnd = this._handleDragEnd.bind(this);
      window.addEventListener('mousemove', this._onMove);
      window.addEventListener('mouseup', this._onEnd);
      this._updateKnob(e.clientX, e.clientY);
    },
    onJoystickTouchStart(e) {
      this.isDragging = true;
      this._onTouchMove = this._handleTouchMove.bind(this);
      this._onTouchEnd = this._handleDragEnd.bind(this);
      window.addEventListener('touchmove', this._onTouchMove, { passive: false });
      window.addEventListener('touchend', this._onTouchEnd);
      if (e.touches.length > 0) {
        this._updateKnob(e.touches[0].clientX, e.touches[0].clientY);
      }
    },
    _handleMouseMove(e) {
      this._updateKnob(e.clientX, e.clientY);
    },
    _handleTouchMove(e) {
      e.preventDefault();
      if (e.touches.length > 0) {
        this._updateKnob(e.touches[0].clientX, e.touches[0].clientY);
      }
    },
    _handleDragEnd() {
      this.isDragging = false;
      this.knobX = 0;
      this.knobY = 0;
      window.removeEventListener('mousemove', this._onMove);
      window.removeEventListener('mouseup', this._onEnd);
      window.removeEventListener('touchmove', this._onTouchMove);
      window.removeEventListener('touchend', this._onTouchEnd);
      this.$emit('currentPos-change', { h: 50, v: 50 });
    },
    _updateKnob(clientX, clientY) {
      const pad = this.$refs.joystickPad;
      if (!pad) return;
      const rect = pad.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const r = this.joystickRadius;
      let dx = clientX - cx;
      let dy = clientY - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > r) {
        dx = (dx / dist) * r;
        dy = (dy / dist) * r;
      }
      this.knobX = dx;
      this.knobY = dy;
      const nx = dx / r; // -1..+1, positive = right
      const ny = dy / r; // -1..+1, positive = down
      this.$emit('currentPos-change', {
        h: 50 + nx * 50,
        v: 50 - ny * 50,
      });
    },
    onWheel(e) {
      let dx, dy;
      if (e.deltaMode === 0) {
        // pixel mode (trackpads, most mice)
        dx = Math.round(e.deltaX / 20);
        dy = Math.round(e.deltaY / 20);
      } else if (e.deltaMode === 1) {
        // line mode
        dx = Math.round(e.deltaX * 3);
        dy = Math.round(e.deltaY * 3);
      } else {
        // page mode
        dx = Math.round(e.deltaX * 10);
        dy = Math.round(e.deltaY * 10);
      }
      if (dx !== 0 || dy !== 0) {
        this.$emit('scroll-by', { dx, dy });
      }
    },
    sendNotification(data) {
      // console.log("emit cell-clicked");
      this.$emit('cell-clicked', data);
    },
  },
};
</script>

<style scoped>
.canvas-workspace {
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.controls-panel {
  display: flex;
  height: 100%;
  gap: 1rem;
}

/* ── Joystick ─────────────────────────────────── */
.nav-control {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding-top: 0.25rem;
}

.joystick-pad {
  position: relative;
  width: 100px;
  height: 100px;
  background: rgba(102, 126, 234, 0.06);
  border: 2px solid rgba(102, 126, 234, 0.25);
  border-radius: 50%;
  cursor: grab;
  user-select: none;
  touch-action: none;
  flex-shrink: 0;
  transition: border-color 0.2s, background 0.2s;
}

.joystick-pad:hover {
  border-color: rgba(102, 126, 234, 0.5);
  background: rgba(102, 126, 234, 0.1);
}

.joystick-pad:active {
  cursor: grabbing;
}

/* subtle crosshair lines */
.joystick-crosshair {
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: 50%;
  overflow: hidden;
}

.joystick-crosshair::before,
.joystick-crosshair::after {
  content: '';
  position: absolute;
  background: rgba(102, 126, 234, 0.18);
}

.joystick-crosshair::before {
  left: 18%;
  right: 18%;
  top: 50%;
  height: 1px;
  margin-top: -0.5px;
}

.joystick-crosshair::after {
  top: 18%;
  bottom: 18%;
  left: 50%;
  width: 1px;
  margin-left: -0.5px;
}

/* inner dashed ring */
.joystick-ring {
  position: absolute;
  inset: 18px;
  border-radius: 50%;
  border: 1px dashed rgba(102, 126, 234, 0.22);
  pointer-events: none;
}

.joystick-knob {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 28px;
  height: 28px;
  margin: -14px 0 0 -14px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  box-shadow: 0 3px 10px rgba(102, 126, 234, 0.4);
  pointer-events: none;
}

.joystick-knob--active {
  box-shadow: 0 5px 18px rgba(102, 126, 234, 0.65);
}

.control-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.joystick-hint {
  font-size: 0.68rem;
  color: rgba(108, 117, 125, 0.65);
  letter-spacing: 0.2px;
  white-space: nowrap;
}

/* ── Main workspace ───────────────────────────── */
.main-workspace {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.workspace-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.coordinates {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  font-family: 'Courier New', monospace;
}

.coordinates-label {
  font-size: 0.75rem;
  color: #6c757d;
}

.canvas-area {
  flex: 1;
  min-height: 0;
  background: #fff;
  border-radius: 12px;
  border: 2px solid rgba(102, 126, 234, 0.1);
  overflow: hidden;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 4px;
}

.position-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  background: rgba(102, 126, 234, 0.05);
  border: 1px solid rgba(102, 126, 234, 0.15);
  border-radius: 8px;
}

/* ── Mobile ───────────────────────────────────── */
@media (max-width: 768px) {
  .controls-panel {
    flex-direction: column;
    gap: 0.5rem;
  }

  .nav-control {
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    padding-top: 0;
  }

  .joystick-pad {
    width: 80px;
    height: 80px;
  }
}
</style>
